import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ChevronDown, Download, FolderPlus, ImagePlus, LogOut, Send, Sparkles, UserPlus, Wand2 } from "lucide-react";
import { useAuth, authFetch } from "../auth";
import { useLang } from "../i18n";
import AuthImage from "../components/AuthImage";

const STATUS_META = [
  ["brief", { en: "Brief", it: "Brief" }],
  ["cad", { en: "CAD Design", it: "Design CAD" }],
  ["prototype", { en: "Prototype", it: "Prototipo" }],
  ["casting", { en: "Casting", it: "Fusione" }],
  ["finishing", { en: "Finishing", it: "Finitura" }],
  ["delivered", { en: "Delivered", it: "Consegnato" }],
  ["recast_requested", { en: "Re-cast requested", it: "Rifusione richiesta" }],
];

const fmtDate = (iso) => new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });

export default function Team() {
  const { client, logout } = useAuth();
  const { L } = useLang();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [detail, setDetail] = useState(null);
  const [reply, setReply] = useState("");
  const [caption, setCaption] = useState("");
  const [busy, setBusy] = useState(false);
  const [newClient, setNewClient] = useState({ name: "", company: "", email: "", password: "" });
  const [newProject, setNewProject] = useState({ client_id: "", title: "", metal: "", description: "", deposit_amount: "", balance_amount: "" });
  const [enquiries, setEnquiries] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [depositEdit, setDepositEdit] = useState("");
  const [balanceEdit, setBalanceEdit] = useState("");
  const fileRef = useRef(null);
  const photoRef = useRef(null);

  useEffect(() => {
    if (client === null) navigate("/login");
    else if (client && client.role !== "team") navigate("/portal");
  }, [client, navigate]);

  const load = useCallback(async () => {
    const [c, p, enq, bkg] = await Promise.all([
      authFetch("/team/clients"),
      authFetch("/team/projects"),
      authFetch("/team/enquiries"),
      authFetch("/team/bookings"),
    ]);
    if (c.ok) setClients(await c.json());
    if (p.ok) setProjects(await p.json());
    if (enq.ok) setEnquiries(await enq.json());
    if (bkg.ok) setBookings(await bkg.json());
  }, []);

  const loadDetail = useCallback(async (pid) => {
    const r = await authFetch(`/team/projects/${pid}`);
    if (r.ok) setDetail(await r.json());
  }, []);

  useEffect(() => {
    if (client?.role === "team") load();
  }, [client, load]);

  useEffect(() => {
    if (expanded) loadDetail(expanded);
  }, [expanded, loadDetail]);

  useEffect(() => {
    setDepositEdit(detail?.project?.deposit_amount ? String(detail.project.deposit_amount) : "");
    setBalanceEdit(detail?.project?.balance_amount ? String(detail.project.balance_amount) : "");
  }, [detail?.project?.id, detail?.project?.deposit_amount, detail?.project?.balance_amount]);

  if (!client || client.role !== "team") {
    return <div className="flex min-h-[60vh] items-center justify-center text-[#8A8F82]">···</div>;
  }

  const createClient = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await authFetch("/team/clients", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(newClient),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(typeof d.detail === "string" ? d.detail : "error");
      toast.success(L({ en: `Client account created for ${newClient.email}`, it: `Account creato per ${newClient.email}` }));
      setNewClient({ name: "", company: "", email: "", password: "" });
      await load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const createProject = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await authFetch("/team/projects", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...newProject, deposit_amount: parseFloat(newProject.deposit_amount) || 0, balance_amount: parseFloat(newProject.balance_amount) || 0 }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(typeof d.detail === "string" ? d.detail : "error");
      toast.success(L({ en: "Project opened.", it: "Progetto aperto." }));
      setNewProject({ client_id: "", title: "", metal: "", description: "", deposit_amount: "", balance_amount: "" });
      await load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const setStatus = async (pid, status) => {
    await authFetch(`/team/projects/${pid}/status`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status }),
    });
    await load();
    if (expanded === pid) await loadDetail(pid);
  };

  const sendReply = async (e) => {
    e.preventDefault();
    if (!reply.trim()) return;
    setBusy(true);
    try {
      await authFetch(`/team/projects/${expanded}/messages`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ body: reply }),
      });
      setReply("");
      await loadDetail(expanded);
    } finally {
      setBusy(false);
    }
  };

  const uploadFor = (pid) => async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      const r = await authFetch(`/team/projects/${pid}/files`, { method: "POST", body: fd });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(typeof d.detail === "string" ? d.detail : "upload failed");
      }
      toast.success(L({ en: "File shared with the client.", it: "File condiviso con il cliente." }));
      if (expanded === pid) await loadDetail(pid);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  const postPhoto = async (pid, file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const up = await authFetch(`/team/projects/${pid}/files`, { method: "POST", body: fd });
      if (!up.ok) throw new Error("upload failed");
      const { id } = await up.json();
      await authFetch(`/team/projects/${pid}/messages`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ body: caption || L({ en: "Photo update from the bench", it: "Aggiornamento fotografico dal banco" }), file_id: id }),
      });
      setCaption("");
      toast.success(L({ en: "Photo update posted.", it: "Aggiornamento fotografico pubblicato." }));
      if (expanded === pid) await loadDetail(pid);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
      if (photoRef.current) photoRef.current.value = "";
    }
  };

  const savePayment = async (kind) => {
    const amount = parseFloat(kind === "deposit" ? depositEdit : balanceEdit) || 0;
    setBusy(true);
    try {
      const r = await authFetch(`/team/projects/${expanded}/payment/${kind}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ amount }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(typeof d.detail === "string" ? d.detail : "error");
      toast.success(L({ en: "Payment amount updated.", it: "Importo aggiornato." }));
      await load();
      await loadDetail(expanded);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const downloadTeamReceipt = async (sessionId) => {
    const r = await authFetch(`/team/receipts/${sessionId}/download`);
    if (!r.ok) return toast.error(L({ en: "Receipt unavailable.", it: "Ricevuta non disponibile." }));
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ricevuta-${sessionId.slice(-8)}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const summarizeEnquiry = async (id) => {
    setBusy(true);
    try {
      const r = await authFetch(`/team/enquiries/${id}/summarize`, { method: "POST" });
      const d = await r.json();
      if (!r.ok) throw new Error(typeof d.detail === "string" ? d.detail : "error");
      setEnquiries((prev) => prev.map((x) => (x.id === id ? { ...x, ai_summary: d.summary } : x)));
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const draftReply = async () => {
    setBusy(true);
    try {
      const r = await authFetch(`/team/projects/${expanded}/draft-reply`, { method: "POST" });
      const d = await r.json();
      if (!r.ok) throw new Error(typeof d.detail === "string" ? d.detail : "error");
      setReply(d.draft);
      toast.success(L({ en: "Draft ready — review and send.", it: "Bozza pronta — rivedila e inviala." }));
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const downloadTeamFile = async (id, name) => {    const r = await authFetch(`/team/files/${id}/download`);
    if (!r.ok) return toast.error(L({ en: "File unavailable.", it: "File non disponibile." }));
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = name || "file";
    a.click();
    URL.revokeObjectURL(url);
  };

  const filesById = Object.fromEntries((detail?.files || []).map((f) => [f.id, f]));

  return (
    <div className="mx-auto max-w-7xl px-6 py-14" data-testid="team-page">
      <div className="mb-10 flex flex-wrap items-end justify-between gap-6">
        <div>
          <p className="mono-tech mb-3 text-[#A8853B]">{L({ en: "Atelier Console", it: "Console Atelier" })}</p>
          <h1 className="font-display text-3xl text-[#0E3B33] sm:text-4xl" data-testid="team-greeting">
            {L({ en: "Ciao,", it: "Ciao," })} {client.name}
          </h1>
        </div>
        <button data-testid="team-logout-btn" onClick={() => { logout(); navigate("/"); }} className="ghost-btn !py-2.5 !px-5 text-sm">
          <LogOut className="h-4 w-4" /> {L({ en: "Sign out", it: "Esci" })}
        </button>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <form onSubmit={createClient} className="rounded-xl border border-[#0E3B33]/10 bg-white p-6" data-testid="team-create-client-form">
          <p className="mono-tech mb-4 flex items-center gap-2 text-[#A8853B]"><UserPlus className="h-4 w-4" /> {L({ en: "New client account", it: "Nuovo account cliente" })}</p>
          <div className="grid gap-3 sm:grid-cols-2">
            <input required data-testid="newclient-name" className="forge-field" placeholder={L({ en: "Full name", it: "Nome completo" })} value={newClient.name} onChange={(e) => setNewClient({ ...newClient, name: e.target.value })} />
            <input data-testid="newclient-company" className="forge-field" placeholder={L({ en: "Company / brand", it: "Azienda / brand" })} value={newClient.company} onChange={(e) => setNewClient({ ...newClient, company: e.target.value })} />
            <input required type="email" data-testid="newclient-email" className="forge-field" placeholder="Email" value={newClient.email} onChange={(e) => setNewClient({ ...newClient, email: e.target.value })} />
            <input required data-testid="newclient-password" className="forge-field" placeholder={L({ en: "Temporary password (min 8)", it: "Password temporanea (min 8)" })} value={newClient.password} onChange={(e) => setNewClient({ ...newClient, password: e.target.value })} />
          </div>
          <button type="submit" disabled={busy} data-testid="newclient-submit" className="ember-btn mt-4 !py-2.5 !px-5 text-sm disabled:opacity-60">
            {L({ en: "Create account", it: "Crea account" })}
          </button>
        </form>

        <form onSubmit={createProject} className="rounded-xl border border-[#0E3B33]/10 bg-white p-6" data-testid="team-create-project-form">
          <p className="mono-tech mb-4 flex items-center gap-2 text-[#A8853B]"><FolderPlus className="h-4 w-4" /> {L({ en: "Open a project", it: "Apri un progetto" })}</p>
          <div className="grid gap-3 sm:grid-cols-2">
            <select required data-testid="newproject-client" className="forge-field appearance-none cursor-pointer" value={newProject.client_id} onChange={(e) => setNewProject({ ...newProject, client_id: e.target.value })}>
              <option value="">{L({ en: "Select client", it: "Seleziona cliente" })}</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name} — {c.email}</option>)}
            </select>
            <input required data-testid="newproject-title" className="forge-field" placeholder={L({ en: "Project title", it: "Titolo del progetto" })} value={newProject.title} onChange={(e) => setNewProject({ ...newProject, title: e.target.value })} />
            <input data-testid="newproject-metal" className="forge-field" placeholder={L({ en: "Metal (e.g. Gold 18K)", it: "Metallo (es. Oro 18K)" })} value={newProject.metal} onChange={(e) => setNewProject({ ...newProject, metal: e.target.value })} />
            <input data-testid="newproject-description" className="forge-field" placeholder={L({ en: "Short description", it: "Breve descrizione" })} value={newProject.description} onChange={(e) => setNewProject({ ...newProject, description: e.target.value })} />
            <input type="number" min="0" step="0.01" data-testid="newproject-deposit" className="forge-field" placeholder={L({ en: "Deposit € (optional)", it: "Acconto € (opzionale)" })} value={newProject.deposit_amount} onChange={(e) => setNewProject({ ...newProject, deposit_amount: e.target.value })} />
            <input type="number" min="0" step="0.01" data-testid="newproject-balance" className="forge-field" placeholder={L({ en: "Balance € (optional)", it: "Saldo € (opzionale)" })} value={newProject.balance_amount} onChange={(e) => setNewProject({ ...newProject, balance_amount: e.target.value })} />
          </div>
          <button type="submit" disabled={busy} data-testid="newproject-submit" className="ember-btn mt-4 !py-2.5 !px-5 text-sm disabled:opacity-60">
            {L({ en: "Open project", it: "Apri progetto" })}
          </button>
        </form>
      </div>

      <div className="mt-10 space-y-4" data-testid="team-project-list">
        {projects.map((p) => (
          <div key={p.id} className="rounded-xl border border-[#0E3B33]/10 bg-white" data-testid={`team-project-${p.id}`}>
            <div className="flex flex-wrap items-center gap-4 p-5">
              <button
                data-testid={`team-expand-${p.id}`}
                onClick={() => setExpanded(expanded === p.id ? null : p.id)}
                className="flex min-w-0 flex-1 items-center gap-3 text-left"
              >
                <ChevronDown className={`h-4 w-4 shrink-0 text-[#A8853B] transition-transform ${expanded === p.id ? "rotate-180" : ""}`} />
                <div className="min-w-0">
                  <div className="truncate font-display text-lg text-[#0E3B33]">{p.title}</div>
                  <div className="truncate text-xs text-[#8A8F82]">{p.client?.name} · {p.client?.email}{p.metal ? ` · ${p.metal}` : ""}</div>
                </div>
              </button>
              <select
                data-testid={`team-status-${p.id}`}
                value={p.status}
                onChange={(e) => setStatus(p.id, e.target.value)}
                className="forge-field !w-auto cursor-pointer appearance-none !py-1.5 text-xs"
              >
                {STATUS_META.map(([k, l]) => <option key={k} value={k}>{L(l)}</option>)}
              </select>
              <label className="ghost-btn !py-1.5 !px-4 cursor-pointer text-xs" data-testid={`team-deliver-${p.id}`}>
                <Download className="h-3.5 w-3.5 rotate-180" /> {L({ en: "Share file", it: "Condividi file" })}
                <input type="file" className="hidden" accept=".jpg,.jpeg,.png,.pdf,.dxf,.stl,.obj,.3dm,.zip" onChange={uploadFor(p.id)} />
              </label>
            </div>

            {expanded === p.id && detail && detail.project.id === p.id && (
              <div className="border-t border-[#0E3B33]/10 p-5">
                <div className="mono-tech mb-4 text-[#8A8F82]">{L({ en: "Thread & updates", it: "Conversazione e aggiornamenti" })}</div>
                <div className="max-h-96 space-y-4 overflow-y-auto pr-1" data-testid="team-thread">
                  {(detail.messages || []).map((m) => (
                    <div key={m.id} className={`flex ${m.author === "team" ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[80%] rounded-lg px-4 py-3 text-sm leading-relaxed ${
                        m.author === "team" ? "bg-[#0E3B33] text-[#FBF9F3]" : m.author === "system" ? "border border-amber-200 bg-amber-50 text-amber-800" : "bg-[#F2EDE3] text-[#1E2B26]"
                      }`}>
                        <div className="mono-tech mb-1 opacity-70">{m.author === "team" ? client.name : m.author === "system" ? L({ en: "System", it: "Sistema" }) : p.client?.name}</div>
                        {m.file_id && filesById[m.file_id]?.content_type?.startsWith("image/") && (
                          <AuthImage team fileId={m.file_id} alt={m.body} className="mb-2 max-h-52 rounded-md object-cover" />
                        )}
                        {m.body}
                        <div className="mono-tech mt-1.5 opacity-50">{fmtDate(m.created_at)}</div>
                      </div>
                    </div>
                  ))}
                </div>
                <form onSubmit={sendReply} className="mt-4 flex gap-3">
                  <input data-testid="team-reply-input" className="forge-field" placeholder={L({ en: "Reply to client…", it: "Rispondi al cliente…" })} value={reply} onChange={(e) => setReply(e.target.value)} />
                  <button type="button" disabled={busy} onClick={draftReply} data-testid="team-draft-reply-btn" className="ghost-btn shrink-0 !px-4 text-xs disabled:opacity-50" title={L({ en: "Draft with AI", it: "Bozza con AI" })}>
                    <Wand2 className="h-4 w-4" />
                  </button>
                  <button type="submit" disabled={busy || !reply.trim()} data-testid="team-reply-send" className="ember-btn shrink-0 !px-5 disabled:opacity-60">
                    <Send className="h-4 w-4" />
                  </button>
                </form>
                <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-[#0E3B33]/10 pt-4">
                  <input data-testid="team-photo-caption" className="forge-field !w-auto flex-1" placeholder={L({ en: "Photo caption (optional)", it: "Didascalia foto (opzionale)" })} value={caption} onChange={(e) => setCaption(e.target.value)} />
                  <label className="ghost-btn !py-2 !px-4 cursor-pointer text-xs" data-testid="team-photo-upload">
                    <ImagePlus className="h-3.5 w-3.5" /> {L({ en: "Post photo update", it: "Pubblica foto" })}
                    <input ref={photoRef} type="file" className="hidden" accept=".jpg,.jpeg,.png" onChange={(e) => postPhoto(p.id, e.target.files?.[0])} />
                  </label>
                </div>
                {["deposit", "balance"].map((k) => {
                  const amount = detail.project[`${k}_amount`] || 0;
                  const pstatus = detail.project[`${k}_status`] || "none";
                  const paidAt = detail.project[`${k}_paid_at`];
                  const editVal = k === "deposit" ? depositEdit : balanceEdit;
                  const setEdit = k === "deposit" ? setDepositEdit : setBalanceEdit;
                  return (
                    <div key={k} className="mt-4 flex flex-wrap items-center gap-3 border-t border-[#0E3B33]/10 pt-4" data-testid={`team-${k}-row`}>
                      <span className="mono-tech w-20 text-[#8A8F82]">{k === "deposit" ? L({ en: "Deposit", it: "Acconto" }) : L({ en: "Balance", it: "Saldo" })}</span>
                      {pstatus === "paid" ? (
                        <span className="text-sm font-medium text-emerald-700" data-testid={`team-${k}-paid`}>
                          €{Number(amount).toFixed(2)} — {L({ en: "paid", it: "pagato" })}
                          {paidAt ? ` · ${fmtDate(paidAt)}` : ""}
                        </span>
                      ) : (
                        <>
                          <input
                            type="number" min="0" step="0.01"
                            data-testid={`team-${k}-input`}
                            className="forge-field !w-32 !py-1.5 text-sm"
                            placeholder="0.00"
                            value={editVal}
                            onChange={(e) => setEdit(e.target.value)}
                          />
                          <button data-testid={`team-${k}-save`} disabled={busy} onClick={() => savePayment(k)} className="ghost-btn !py-1.5 !px-4 text-xs disabled:opacity-60">
                            {L({ en: "Set", it: "Imposta" })}
                          </button>
                          {amount > 0 && (
                            <span className="mono-tech text-amber-700">{L({ en: "awaiting payment", it: "in attesa di pagamento" })}</span>
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
                {(detail.payments || []).length > 0 && (
                  <div className="mt-4 border-t border-[#0E3B33]/10 pt-4" data-testid="team-receipts">
                    <span className="mono-tech text-[#8A8F82]">{L({ en: "Receipts", it: "Ricevute" })}</span>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {detail.payments.map((p) => (
                        <button
                          key={p.session_id}
                          data-testid={`team-receipt-${p.session_id.slice(-8)}`}
                          onClick={() => downloadTeamReceipt(p.session_id)}
                          className="flex items-center gap-2 rounded-full border border-[#0E3B33]/15 px-4 py-1.5 text-xs text-[#4A5248] transition-colors hover:border-[#C9A84C]"
                        >
                          <Download className="h-3.5 w-3.5" />
                          {(p.kind === "deposit" ? L({ en: "Deposit", it: "Acconto" }) : L({ en: "Balance", it: "Saldo" }))} €{(p.amount / 100).toFixed(2)} · {p.session_id.slice(-8).toUpperCase()}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
        {projects.length === 0 && (
          <div className="rounded-xl border border-[#0E3B33]/10 bg-white p-12 text-center text-sm text-[#8A8F82]" data-testid="team-empty">
            {L({ en: "No projects yet — create a client account above, then open their first project.", it: "Nessun progetto ancora — crea un account cliente qui sopra, poi apri il suo primo progetto." })}
          </div>
        )}
      </div>

      <div className="mt-12" data-testid="team-inbox">
        <p className="mono-tech mb-5 text-[#A8853B]">{L({ en: "Inbox — Website briefs & consultations", it: "Posta — Brief dal sito e consulenze" })}</p>
        <div className="grid gap-6 lg:grid-cols-2">
          <div className="rounded-xl border border-[#0E3B33]/10 bg-white p-6">
            <p className="mono-tech mb-4 text-[#8A8F82]">{L({ en: "Project briefs", it: "Brief di progetto" })} · {enquiries.length}</p>
            <div className="max-h-96 space-y-3 overflow-y-auto pr-1">
              {enquiries.map((enq) => (
                <div key={enq.id} className="rounded-lg border border-[#0E3B33]/10 p-4" data-testid={`inbox-enquiry-${enq.id}`}>
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="text-sm font-medium text-[#0E3B33]">{enq.name}{enq.company ? ` · ${enq.company}` : ""}</span>
                    <span className="mono-tech shrink-0 text-[#8A8F82]">{fmtDate(enq.created_at)}</span>
                  </div>
                  <div className="mt-0.5 text-xs text-[#A8853B]">{enq.email}{enq.country ? ` · ${enq.country}` : ""}</div>
                  {(enq.project_type || enq.material) && (
                    <div className="mono-tech mt-2 text-[#8A8F82]">{[enq.project_type, enq.material, enq.quantity].filter(Boolean).join(" · ")}</div>
                  )}
                  {enq.message && <p className="mt-2 text-sm leading-relaxed text-[#4A5248]">{enq.message}</p>}
                  {enq.ai_summary ? (
                    <div className="mt-3 rounded-md border border-[#C9A84C]/30 bg-[#C9A84C]/[0.07] p-3" data-testid={`enquiry-summary-${enq.id}`}>
                      <div className="mono-tech mb-1.5 flex items-center gap-1.5 text-[#A8853B]"><Sparkles className="h-3 w-3" /> {L({ en: "AI brief summary", it: "Sintesi AI" })}</div>
                      <p className="whitespace-pre-wrap text-xs leading-relaxed text-[#4A5248]">{enq.ai_summary}</p>
                    </div>
                  ) : (
                    <button
                      data-testid={`enquiry-summarize-${enq.id}`}
                      onClick={() => summarizeEnquiry(enq.id)}
                      className="mt-3 flex items-center gap-1.5 text-xs font-medium text-[#A8853B] transition-colors hover:text-[#0E3B33]"
                    >
                      <Sparkles className="h-3 w-3" /> {L({ en: "Generate AI summary", it: "Genera sintesi AI" })}
                    </button>
                  )}
                  {Array.isArray(enq.files) && enq.files.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {enq.files.map((f, i) =>
                        typeof f === "object" && f.id ? (
                          <button
                            key={f.id}
                            data-testid={`enquiry-file-${f.id}`}
                            onClick={() => downloadTeamFile(f.id, f.name)}
                            className="flex items-center gap-1.5 rounded-full border border-[#0E3B33]/15 px-3 py-1 text-xs text-[#4A5248] transition-colors hover:border-[#C9A84C]"
                          >
                            <Download className="h-3 w-3" /> {f.name}
                          </button>
                        ) : (
                          <span key={i} className="mono-tech text-[#8A8F82]">{String(f)}</span>
                        )
                      )}
                    </div>
                  )}
                </div>
              ))}
              {enquiries.length === 0 && <p className="text-sm text-[#8A8F82]">{L({ en: "No briefs yet.", it: "Nessun brief ancora." })}</p>}
            </div>
          </div>
          <div className="rounded-xl border border-[#0E3B33]/10 bg-white p-6">
            <p className="mono-tech mb-4 text-[#8A8F82]">{L({ en: "Consultation requests", it: "Richieste di consulenza" })} · {bookings.length}</p>
            <div className="max-h-96 space-y-3 overflow-y-auto pr-1">
              {bookings.map((b) => (
                <div key={b.id} className="rounded-lg border border-[#0E3B33]/10 p-4" data-testid={`inbox-booking-${b.id}`}>
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="text-sm font-medium text-[#0E3B33]">{b.name}</span>
                    <span className="mono-tech shrink-0 text-[#A8853B]">{b.date} · {b.time_slot}</span>
                  </div>
                  <div className="mt-0.5 text-xs text-[#8A8F82]">{b.email} · {b.consultation_type === "video" ? L({ en: "Video call", it: "Videochiamata" }) : L({ en: "At the atelier", it: "In atelier" })}</div>
                  {b.notes && <p className="mt-2 text-sm leading-relaxed text-[#4A5248]">{b.notes}</p>}
                </div>
              ))}
              {bookings.length === 0 && <p className="text-sm text-[#8A8F82]">{L({ en: "No consultation requests yet.", it: "Nessuna richiesta di consulenza ancora." })}</p>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
